/**
 * The setup checklist at the top of Início: the few things that make Vephus useful, each ticked from
 * the real state of the organization rather than from a click on "done".
 *
 *  1. Connect an agent        — one of the person's own keys has been used, or the org has a session
 *  2. Link a repository       — a repository is linked
 *  3. Sync the documentation  — the docs tree has at least one document
 *  4. Leave the agent listening — remote control is on, or the desktop app is online
 *  5. Send the first work     — there is a work order
 *  6. Build the team          — (orgs with more than one developer) a team of two or more, and the
 *                               person accepts team work
 *
 * Each step not yet done says in one line why it matters and links to exactly where it is done. The
 * card folds, can be dismissed, and goes away by itself once everything is ticked; a small link
 * brings it back. Both choices are remembered in this browser — a nicety, so storage failing only
 * means the card shows again.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Check, ChevronDown, ListChecks, Sparkles, X } from 'lucide-react'
import { sendCommand } from '../lib/dockPlacement'
import { api } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { isDesktop } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import { usePersistedFlag } from '../lib/usePersistedFlag'
import { localAiProbe } from '../lib/localAi'
import { localized, usePromptCatalog } from '../lib/prompts'
import { fillUrl } from '../lib/skillsAndMcps'
import type { Developer, DocTree, MyCredential, PromptQueue, RemoteOverview, RepositoryLink, Team, WorkOrder } from '../lib/types'

export type SetupStepId = 'agent' | 'repository' | 'docs' | 'listening' | 'work' | 'skills' | 'localAi' | 'team'

/** How much a step matters, said with the step (the guided setup for lay users, 15-16/10). */
export type SetupTier = 'must' | 'should' | 'optional'

export interface SetupStep {
  id: SetupStepId
  done: boolean
  /** Where the step is done. */
  to: string
  tier: SetupTier
}

/** What the checklist is worked out from. `undefined` means "not known" (not loaded, or not allowed). */
export interface SetupState {
  credentials?: MyCredential[]
  sessionCount?: number
  repositories?: RepositoryLink[]
  docs?: DocTree
  remote?: RemoteOverview
  queue?: PromptQueue
  workOrders?: WorkOrder[]
  teams?: Team[]
  developers?: Developer[]
  /** The person has a developer profile: agents of their own that can listen and take team work. */
  isDeveloper: boolean
  desktop: boolean
  /** The team's skill library has anything in it (the recommended skills count once installed). */
  hasSkills?: boolean
  /** The free local AI, on the desktop: present only where the guided install exists. */
  localAi?: { ready: boolean }
}

export const DISMISSED_KEY = 'devpulse:setup-dismissed'
export const COLLAPSED_KEY = 'devpulse:setup-collapsed'

/** The steps, in order, with which of them are done. Pure, so it can be tested without a server. */
export function setupSteps(state: SetupState): SetupStep[] {
  const keyUsed = (state.credentials ?? []).some((k) => !k.revokedAt && Boolean(k.lastUsedAt))
  const steps: SetupStep[] = [
    { id: 'agent', done: keyUsed || (state.sessionCount ?? 0) > 0, to: '/agents/connect', tier: 'must' },
    // The free project (docs/refactor/49) lets the person work at once, so a project of their own is advisable then,
    // not required — and the free one is not that project.
    {
      id: 'repository',
      done: (state.repositories ?? []).some((r) => r.linked && !r.isDefault),
      to: '/agents/repositories',
      tier: (state.repositories ?? []).some((r) => r.isDefault) ? 'should' : 'must',
    },
    { id: 'docs', done: (state.docs?.docs.length ?? 0) > 0, to: '/project/docs', tier: 'should' },
  ]

  // Listening needs agents of one's own: somebody with no developer profile has none to switch on.
  if (state.isDeveloper) {
    steps.push({
      id: 'listening',
      done: Boolean(state.remote?.enabled) || Boolean(state.queue?.desktopOnline),
      // In the desktop app the runner is right here; in a browser, remote control is the way.
      to: state.desktop ? '/agents/connect?tab=desktop' : '/agents/remote',
      tier: 'should',
    })
  }

  // The recommended skills (docs/refactor/42; superpowers ships as the project default, the owner's
  // call of 16/10): pre-marked as advisable, installed in one click from the card itself.
  if (state.hasSkills !== undefined) {
    steps.push({ id: 'skills', done: state.hasSkills, to: '/agents/skills', tier: 'should' })
  }

  // The free local AI, only where the guided install exists (the desktop says whether it is ready).
  if (state.localAi) {
    steps.push({ id: 'localAi', done: state.localAi.ready, to: '/agents/connect?tab=desktop', tier: 'optional' })
  }

  // The first request: an order, a command sent, or a session of one's agent (a request in Conversas
  // makes one) — whichever way it went. It is made in Conversas.
  const asked = (state.workOrders ?? []).length > 0 || (state.queue?.items?.length ?? 0) > 0 || (state.sessionCount ?? 0) > 0
  steps.push({ id: 'work', done: asked, to: '/agents/chat', tier: 'must' })

  // A team only matters with somebody to share work with. The list of developers is a manager's
  // view; without it the step is left out rather than guessed.
  const developerCount = (state.developers ?? []).filter((d) => d.isActive).length
  if (developerCount > 1) {
    const hasTeam = (state.teams ?? []).some((team) => (team.memberIds?.length ?? team.developerCount) >= 2)
    // Done with a team: taking commands from teammates is each person's own choice, off by default (docs/refactor/63),
    // and the checklist must not nudge anyone into opening their computer.
    steps.push({ id: 'team', done: hasTeam, to: '/settings/teams', tier: 'optional' })
  }

  return steps
}

const LANGUAGE: Record<string, string> = { 'pt-BR': 'Portuguese (Brazil)', 'en-US': 'English' }

/**
 * The catalog's guide prompt (setup.guide, docs/refactor/44) filled with the checklist as it stands — each step
 * done or missing, with how much it matters — and the person's language, for their own AI to lead the rest.
 */
export function guidePrompt(body: string, steps: SetupStep[], t: (key: string) => string, locale: string): string {
  const list = steps.map((step) => `- [${step.done ? 'x' : ' '}] ${t(`setup.step.${step.id}.title`)} (${step.tier})`).join('\n')
  return body.split('{{language}}').join(LANGUAGE[locale] ?? locale).split('{{steps}}').join(list)
}

export function SetupChecklist() {
  const { t } = useI18n()
  const session = getSession()
  const canManage = atLeast(session?.role, 'EngineeringManager')

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const isDeveloper = Boolean(me?.developerId)

  // The same keys the rest of the app caches these under, so opening Início costs little.
  const credentials = useQuery({ queryKey: ['me', 'credentials'], queryFn: api.me.credentials, retry: false })
  const sessions = useQuery({
    queryKey: ['setup', 'sessions'],
    queryFn: () => api.sessions.list({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 60_000,
  })
  const repositories = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list, retry: false })
  const docs = useQuery({ queryKey: ['docs', 'tree'], queryFn: api.docs.tree, retry: false })
  const remote = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, enabled: isDeveloper, retry: false })
  const queue = useQuery({ queryKey: ['prompt-queue'], queryFn: api.promptQueue.list, enabled: isDeveloper, retry: false })
  const workOrders = useQuery({ queryKey: ['work-orders'], queryFn: api.workOrders.list, retry: false })
  const teams = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams, enabled: canManage, retry: false })
  const skills = useQuery({ queryKey: ['skills'], queryFn: api.skills.list, retry: false })
  const probe = useQuery({ queryKey: ['local-ai-probe'], queryFn: localAiProbe, enabled: isDesktop(), retry: false })
  const { data: catalog } = usePromptCatalog()
  const { locale } = useI18n()
  const [skillsSent, setSkillsSent] = useState(false)
  // "Instalar as skills recomendadas" pre-marked (superpowers as the project default, 16/10): one
  // click sends the person's own AI to install from the official repository.
  const installSkills = useMutation({
    mutationFn: async () => {
      const prompt = catalog?.prompts.find((p) => p.key === 'skills.install-from-repo')
      if (!prompt) throw new Error('catalog')
      return api.workOrders.create({
        kind: 'Once',
        instructions: fillUrl(prompt.body, 'https://github.com/obra/superpowers'),
        title: `${localized(prompt.title, locale)}: superpowers`.slice(0, 200),
        repositoryId: null,
      })
    },
    onSuccess: () => setSkillsSent(true),
  })
  const developers = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers, enabled: canManage, retry: false })

  const [dismissed, toggleDismissed] = usePersistedFlag(DISMISSED_KEY, false)
  const [collapsed, toggleCollapsed] = usePersistedFlag(COLLAPSED_KEY, false)
  // Asked back after it hid itself for being complete: shown for this visit.
  const [revealed, setRevealed] = useState(false)

  // Nothing is drawn until the answers are in, so a finished setup never flashes a half-ticked card.
  const pending = [credentials, sessions, repositories, docs, workOrders].some((q) => q.isPending)
    || (isDeveloper && (remote.isPending || queue.isPending))
    || (canManage && (teams.isPending || developers.isPending))
    || !me
  if (pending) return null

  const steps = setupSteps({
    credentials: credentials.data,
    sessionCount: sessions.data?.total,
    repositories: repositories.data,
    docs: docs.data,
    remote: remote.data,
    queue: queue.data,
    workOrders: workOrders.data,
    teams: teams.data,
    developers: developers.data,
    isDeveloper,
    desktop: isDesktop(),
    hasSkills: skills.data ? skills.data.length > 0 : undefined,
    localAi: probe.data?.supported ? { ready: probe.data.ollamaRunning && probe.data.modelPresent } : undefined,
  })
  const done = steps.filter((step) => step.done).length
  const complete = done === steps.length
  // With an AI connected, it leads the rest in the conversation (the prompt box's Resposta); before that there
  // is nobody to guide, and the first step says how to connect one.
  const guide = catalog?.prompts.find((p) => p.key === 'setup.guide')
  const canGuide = Boolean(guide) && steps.some((step) => step.id === 'agent' && step.done) && !complete

  if (dismissed || (complete && !revealed)) {
    return (
      <div className="setup-show">
        <button
          type="button"
          className="setup-show__link tiny"
          onClick={() => {
            if (dismissed) toggleDismissed()
            setRevealed(true)
          }}
        >
          <ListChecks size={12} aria-hidden /> {t('setup.show')}
        </button>
      </div>
    )
  }

  return (
    <section className={`setup${collapsed ? ' setup--collapsed' : ''}`} aria-labelledby="setup-title">
      <header className="setup__header">
        <button type="button" className="setup__toggle" aria-expanded={!collapsed} aria-controls="setup-steps" onClick={toggleCollapsed}>
          <ListChecks size={16} aria-hidden className="setup__icon" />
          <span className="setup__title" id="setup-title">{t('setup.title')}</span>
          <span className="setup__count tiny">{t('setup.progress', { done, total: steps.length })}</span>
          <ChevronDown size={14} aria-hidden className="setup__chevron" />
        </button>
        <div className="setup__meter" aria-hidden>
          <div className="setup__meter-fill" style={{ width: `${(done / steps.length) * 100}%` }} />
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm"
          onClick={() => {
            if (!dismissed) toggleDismissed()
            setRevealed(false)
          }}
          title={t('setup.dismiss')}
          aria-label={t('setup.dismiss')}
        >
          <X size={14} />
        </button>
      </header>

      {!collapsed && canGuide && guide && (
        <div className="setup__guide">
          <button type="button" className="btn btn--primary setup__guide-button" onClick={() => sendCommand(guidePrompt(guide.body, steps, t, locale), t('setup.guide'))}>
            <Sparkles size={16} aria-hidden /> {t('setup.guide')}
          </button>
          <span className="small muted">{t('setup.guideHint')}</span>
        </div>
      )}

      {!collapsed && (
        <ol className="setup__steps" id="setup-steps">
          {steps.map((step, index) => (
            <li key={step.id} className={`setup-step${step.done ? ' setup-step--done' : ''}`}>
              <span className="setup-step__mark" aria-hidden>
                {step.done ? <Check size={13} /> : index + 1}
              </span>
              <div className="setup-step__text">
                <span className="setup-step__title">
                  {t(`setup.step.${step.id}.title`)}
                  <span className={`setup-step__tier tiny setup-step__tier--${step.tier}`}>{t(`setup.tier.${step.tier}`)}</span>
                  <span className="sr-only"> — {step.done ? t('setup.done') : t('setup.todo')}</span>
                </span>
                {!step.done && <span className="setup-step__hint tiny">{t(`setup.step.${step.id}.hint`)}</span>}
              </div>
              {!step.done && step.id === 'skills' ? (
                skillsSent ? (
                  <span className="tiny muted">{t('setup.skillsSent')}</span>
                ) : (
                  <button type="button" className="btn btn--sm setup-step__action" onClick={() => installSkills.mutate()} disabled={installSkills.isPending}>
                    {t('setup.step.skills.action')} <ArrowRight size={13} aria-hidden />
                  </button>
                )
              ) : !step.done && (
                <Link to={step.to} className="btn btn--sm setup-step__action">
                  {t(`setup.step.${step.id}.action`)} <ArrowRight size={13} aria-hidden />
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
